import { createContext, useState, useEffect } from "react";

import {
    InteractionRequiredAuthError,
    InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { loginRequestScope, accessTokenScope } from "authConfig";
import { APP_UI_URL } from "config";
import { useDispatch } from 'store';

const AzureMSALContext = createContext(null);

const SELECTED_ROLE_STORAGE_KEY = 'selected_role';

// Role-naming convention used by the FUMES Azure AD app registration:
//   - Bare "Admin"        -> Global Admin (cross-site)
//   - "Admin.<x>"         -> Admin-side variant (treated as ADMIN site, not global)
//   - "<Site>.<RoleName>" -> ordinary site-bound role (e.g. "CGY.User", "TBY.Admin")
//
// Highest-privilege ordering: Global Admin > Admin.* > <Site>.Admin > first listed.
// Used to pick the default active role on first login when the user has multiple.
const pickHighestPrivilegeRole = (rolesArr) => {
    if (!rolesArr || rolesArr.length === 0) return null;

    const globalAdmin = rolesArr.find((r) => r === 'Admin');
    if (globalAdmin) return globalAdmin;

    const adminVariant = rolesArr.find((r) => r.startsWith('Admin.'));
    if (adminVariant) return adminVariant;

    const siteAdmin = rolesArr.find((r) => r.endsWith('.Admin'));
    if (siteAdmin) return siteAdmin;

    return rolesArr[0];
};

// Derive { site, roleName, isGlobalAdmin } from a single role string. Single
// source of truth: every downstream consumer reads these three values from
// context, so toggling the active role here propagates everywhere without
// touching consumer code.
const deriveStateFromRole = (role) => {
    if (!role) {
        return { site: null, roleName: null, isGlobalAdmin: false };
    }
    const parts = role.split('.');

    if (role === 'Admin' && parts.length === 1) {
        return { site: 'ADMIN', roleName: 'Admin', isGlobalAdmin: true };
    }
    if (parts[0] === 'Admin') {
        return { site: 'ADMIN', roleName: parts[1] || 'Admin', isGlobalAdmin: false };
    }
    return { site: parts[0], roleName: parts[1] || null, isGlobalAdmin: false };
};

// Display-friendly label for a role string. Exported so Header + ProfileSection
// render the active-role chip / dropdown items the same way.
export const formatRoleLabel = (role) => {
    if (!role) return '';
    if (role === 'Admin') return 'Project Admin';
    const parts = role.split('.');
    if (parts[0] === 'Admin') return `Admin ${parts[1] || ''}`.trim();
    return `${parts[0]} ${parts[1] || ''}`.trim();
};

export const AzureMSALProvider = ({ children }) => {

    const { instance, accounts, inProgress } = useMsal();
    const [accessToken, setAccessToken] = useState(null);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userSite, setUserSite] = useState(null);
    const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
    const [error, setError] = useState(null);
    const [isRefreshingToken, setIsRefreshingToken] = useState(false);
    const loginType = "redirect";

    const hasValidToken = () => {
        const token = sessionStorage.getItem('access_token');
        if (token !== "" && token !== null && token !== undefined) {
            return true;
        } else {
            getToken();
            return false
        }
    }

    const login = async () => {
        try {
            if (loginType === "popup") {
                const result = await instance.loginPopup(loginRequestScope).catch(e => {
                    console.log(e);
                });
            } else if (loginType === "redirect") {
                const result = instance.loginRedirect(loginRequestScope).catch(e => {
                    console.log(e);
                });
            }
        } catch (e) {
            setError(e.message);
        }
    };

    const logout = async () => {
        try {
            sessionStorage.removeItem("access_token");
            sessionStorage.removeItem(SELECTED_ROLE_STORAGE_KEY);
            // Reset role state so a subsequent login on the same provider instance
            // re-seeds from the new token rather than preserving the previous
            // session's selection (matters if another user logs in on this tab
            // without a full page reload).
            setRoles([]);
            setSelectedRole(null);
            setUserSite(null);
            setUserRole(null);
            setIsGlobalAdmin(false);
            instance.logoutRedirect({
                postLogoutRedirectUri: APP_UI_URL
            })
            await instance.logout();
        } catch (e) {
            sessionStorage.removeItem("access_token");
            sessionStorage.removeItem(SELECTED_ROLE_STORAGE_KEY);
            console.error(e);
            setError(e.message);
        }
    };

    const getToken = () => {
        const accessTokenRequest = {
            ...accessTokenScope,
            account: accounts[0]
        };

        if (inProgress === InteractionStatus.None) {
            instance
                .acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => {
                    // Acquire token silent success
                    setAccessToken(accessTokenResponse.accessToken);
                    sessionStorage.setItem("access_token", accessTokenResponse.accessToken);
                    sessionStorage.setItem("token_expiry", accessTokenResponse.expiresOn);
                    scheduleRefreshToken(accessTokenResponse.expiresOn);
                    return accessTokenResponse.accessToken
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance
                            .acquireTokenRedirect(accessTokenRequest)
                            .then((accessTokenResponse) => {
                                setAccessToken(accessTokenResponse.accessToken);
                                sessionStorage.setItem("access_token", accessTokenResponse.accessToken);
                                sessionStorage.setItem("token_expiry", accessTokenResponse.expiresOn);
                                scheduleRefreshToken(accessTokenResponse.expiresOn);
                                return accessTokenResponse.accessToken;
                            });
                    }
                    console.log(error);
                });
        }
    };

    const scheduleRefreshToken = (expiresOn) => {
        const delay = expiresOn - Date.now() - 30000; // Refresh token 30 seconds before expiration
        setTimeout(refreshToken, delay);
    }

    const refreshToken = () => {
        setIsRefreshingToken(true);
        console.log("Token Expired Refreshing Token");
        instance
            .acquireTokenSilent({
                ...accessTokenScope,
                account: accounts[0]
            })
            .then((accessTokenResponse) => {
                setAccessToken(accessTokenResponse.accessToken);
                sessionStorage.setItem("access_token", accessTokenResponse.accessToken);
                sessionStorage.setItem("token_expiry", accessTokenResponse.expiresOn);
                scheduleRefreshToken(accessTokenResponse.expiresOn);
            })
            .catch((error) => {
                setIsRefreshingToken(false);
                setError(error);
            });
    }

    // Switch the active role. Validates the requested role is one the user
    // actually holds, updates all three derived fields atomically so any
    // dependent effect (e.g. the dashboard prodorder fetch keyed off userSite)
    // sees a consistent snapshot on the next render, and persists the choice
    // so it survives reloads inside the MSAL session.
    const switchRole = (nextRole) => {
        if (!nextRole || !roles.includes(nextRole)) return;
        const derived = deriveStateFromRole(nextRole);
        setSelectedRole(nextRole);
        setUserSite(derived.site);
        setUserRole(derived.roleName);
        setIsGlobalAdmin(derived.isGlobalAdmin);
        sessionStorage.setItem(SELECTED_ROLE_STORAGE_KEY, nextRole);
    };

    // On account load (initial sign-in AND silent token renewals that bump the
    // accounts reference): refresh the roles list and pick which one is active.
    //
    // Important: silent token renewals fire every ~55 minutes and re-trigger
    // this effect. If we unconditionally re-seeded the active role, the user's
    // switcher choice would get clobbered back to the default on every renewal.
    // The early-return below preserves the live selection when the token still
    // grants it. Roles array is still refreshed in case Azure changed grants.
    //
    // Resolution order on a fresh seed:
    //   1. If sessionStorage has a previously-selected role AND it's still in
    //      the token's roles claim, keep it (preserves choice across reloads).
    //   2. Otherwise default to the highest-privilege role available.
    useEffect(() => {
        if (accounts.length > 0) {
            getToken();

            if (accounts[0].idTokenClaims !== undefined && accounts[0].idTokenClaims !== null &&
                accounts[0].idTokenClaims.roles !== undefined && accounts[0].idTokenClaims.roles !== null) {
                const tokenRoles = accounts[0].idTokenClaims.roles;

                // Refresh local roles array only when token actually changed it
                // (defensive copy avoids holding MSAL's internal array reference).
                const rolesChanged = roles.length !== tokenRoles.length
                    || !tokenRoles.every((r) => roles.includes(r));
                if (rolesChanged) {
                    setRoles([...tokenRoles]);
                }

                // Live selection still granted by the token => preserve it.
                // This is the silent-renewal case; without this guard, the
                // switcher choice would reset every refresh cycle.
                if (selectedRole && tokenRoles.includes(selectedRole)) {
                    return;
                }

                const persisted = sessionStorage.getItem(SELECTED_ROLE_STORAGE_KEY);
                const initialRole = persisted && tokenRoles.includes(persisted)
                    ? persisted
                    : pickHighestPrivilegeRole(tokenRoles);

                if (initialRole) {
                    const derived = deriveStateFromRole(initialRole);
                    setSelectedRole(initialRole);
                    setUserSite(derived.site);
                    setUserRole(derived.roleName);
                    setIsGlobalAdmin(derived.isGlobalAdmin);
                    sessionStorage.setItem(SELECTED_ROLE_STORAGE_KEY, initialRole);
                }
            }
        }
    }, [accounts]);

    return (
        <AzureMSALContext.Provider
            value={{
                error,
                login,
                logout,
                getToken,
                accessToken,
                hasValidToken,
                userRole,
                userSite,
                isGlobalAdmin,
                roles,
                selectedRole,
                switchRole
            }}
        >
            {children}
        </AzureMSALContext.Provider>
    );
};

export default AzureMSALContext;
